/**
 * View for a single curated section in the Global Search modal.
 *
 * Pure presentation: section heading, optional spinner, list of shared
 * PlayerSearchResultRow rows. Empty sections are hidden upstream by the
 * Container so this view always has something to render.
 * @module features/global-search/components/GlobalSearchCuratedSections/CuratedSection/CuratedSectionView
 */

import React, { memo } from "react";

import PlayerSearchResultRow from "@/components/organisms/shared/search-modal/PlayerSearchResultRow";
import { Box } from "@/components/atoms/Box";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { type PlayerSearchPlayerFragment } from "@/generated/graphql";

/** Props for the CuratedSectionView */
interface CuratedSectionViewProps {
  /** Section heading text */
  readonly title: string;
  /** Players to render */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether to show the spinner instead of the row list */
  readonly loading: boolean;
  /** Press handlers keyed by player id */
  readonly pressHandlers: Readonly<Record<string, () => void>>;
  /** Action slot renderer per row */
  readonly renderAction: (
    player: PlayerSearchPlayerFragment
  ) => React.ReactNode;
  /** Active sort field for the metric badge */
  readonly effectiveSortBy: string;
  /** Display label for the sort field */
  readonly sortFieldLabel: string;
}

/**
 * Renders a single curated section
 * @param root0 - Component props
 * @param root0.title - Section heading
 * @param root0.players - Players to render
 * @param root0.loading - Whether to show spinner
 * @param root0.pressHandlers - Per-player press handlers
 * @param root0.renderAction - Action slot renderer
 * @param root0.effectiveSortBy - Active sort field
 * @param root0.sortFieldLabel - Sort field display label
 */
const CuratedSectionView = ({
  title,
  players,
  loading,
  pressHandlers,
  renderAction,
  effectiveSortBy,
  sortFieldLabel,
}: CuratedSectionViewProps) => (
  <Stack>
    <Box className="px-6 pb-2 pt-4">
      <Text
        variant="unstyled"
        className="font-medium text-caption uppercase text-content-muted"
      >
        {title}
      </Text>
    </Box>
    {loading ? (
      <Box className="items-center py-6">
        <Spinner size="small" />
      </Box>
    ) : (
      players.map(player => (
        <PlayerSearchResultRow
          key={player.id}
          player={player}
          effectiveSortBy={effectiveSortBy}
          sortFieldLabel={sortFieldLabel}
          onPress={pressHandlers[player.id]}
          action={renderAction(player)}
        />
      ))
    )}
  </Stack>
);

CuratedSectionView.displayName = "CuratedSectionView";

export default memo(CuratedSectionView);
